import { createAsyncThunk } from "@reduxjs/toolkit"

import apiClient from "src/apiClient"
import i18n from "src/i18n"
import { playerFacingError } from "src/utils/errorMessages"
import { addMessage } from "src/store/messages"

import userReducers from "../reducers"

/* Taking a new name. The answer is the whole user, so `nextUsernameChangeAt` arrives with the name
   itself and the profile disables the button from the same response that renamed the player —
   there is no moment where the new name is on screen beside a button that says it may be changed.

   **Refusals are returned, not announced.** Every one of them — taken, too soon, mid-game — is
   about the word the player just typed, so it belongs beside the field and not in a toast that
   floats over the map. The editor renders what this returns; `handlesOwnErrors` keeps the axios
   interceptor from adding its own "Request failed with status code 409" on top, which is the
   sentence the player was reading in the screenshot that prompted this.

   The refusal is translated *here* rather than taken from the server's `detail`, through the same
   `errorMessages.js` map every socket refusal goes through — that is what makes it Spanish on a
   Spanish profile. `available_on` is interpolated into the cooldown's sentence. */
export const thunk = createAsyncThunk("user/changeUsername", async (username, { dispatch, rejectWithValue }) => {
  try {
    const response = await apiClient.patch(
      "/users/me/username/",
      { username },
      { handlesOwnErrors: true }
    )
    dispatch(addMessage({ text: i18n.t("settings.usernameChanged"), type: "success" }))
    return {
      username: response.data.username,
      nextUsernameChangeAt: response.data.next_username_change_at,
    }
  } catch (error) {
    const data = error.response?.data
    // A shape the server rejected comes back keyed by the field, already about the field. Anything
    // else is a rule, and arrives as a code to look up.
    const fieldError = Array.isArray(data?.username) ? data.username.join(" ") : null
    return rejectWithValue({
      message:
        fieldError ??
        playerFacingError(data?.code, {
          date: data?.available_on
            ? new Date(data.available_on).toLocaleDateString(i18n.language, {
                day: "numeric",
                month: "long",
              })
            : undefined,
        }),
    })
  }
})

export const onSucceed = userReducers.setData

const thunkObject = { thunk, onSucceed }
export default thunkObject
